/**
 * Модуль для рендеринга UI квеста Errorborn
 */

/**
 * Форматирует дату для отображения
 */
function formatDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

/**
 * Создаёт рендерер для UI квеста Errorborn
 */
export function createErrorbornQuestRenderer({
  escapeHtml,
  formatDateFn = formatDate,
  documentRef = document,
}) {
  /**
   * Рендерит карточку квеста Errorborn для главного экрана
   */
  function renderErrorbornQuestCard(status) {
    const container = documentRef.getElementById("errorborn-quest-card");
    if (!container) return;

    if (!status) {
      container.innerHTML = '<div class="loading">Подключение к Errorborn...</div>';
      return;
    }

    const {
      totalFragments,
      requiredFragments,
      state,
      claimable,
      missingFragments = [],
      fragments = [],
      claimedAt,
    } = status;

    const collected = Math.min(totalFragments, requiredFragments);
    const progress = requiredFragments > 0
      ? Math.min(100, Math.round((collected / requiredFragments) * 100))
      : 0;

    let title = "Соберите все фрагменты";
    let buttonText = "Подробнее";

    if (state === "portal_ready" && claimable) {
      title = "Портал готов — Errorborn ждёт призыва";
      buttonText = "Открыть портал";
    } else if (state === "claimed") {
      title = "Errorborn призван";
      buttonText = "Открыть хронику";
    } else if (state === "portal_ready") {
      title = "Портал открыт";
      buttonText = "Продолжить ритуал";
    }

    let infoText = "";
    if (state === "claimed" && claimedAt) {
      infoText = `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 6px;">Призван: ${formatDateFn(claimedAt)}</div>`;
    } else if (missingFragments.length > 0) {
      const missingNames = missingFragments
        .slice(0, 3)
        .map(
          (fragment) =>
            escapeHtml(
              fragment?.name || fragment?.baseId || "Фрагмент",
            ),
        )
        .join(", ");
      infoText = `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 6px;">Осталось: ${missingNames}${missingFragments.length > 3 ? "…" : ""}</div>`;
    } else if (fragments.length > 0) {
      const lastFragment = fragments[fragments.length - 1];
      infoText = `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 6px;">Последний фрагмент: ${escapeHtml(lastFragment?.name || lastFragment?.baseId || "-")}</div>`;
    }

    container.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; gap: 8px;">
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="font-size: 12px; font-weight: 600; color: rgba(255,255,255,0.85); background: linear-gradient(135deg, rgba(156, 39, 176, 0.85), rgba(33, 150, 243, 0.85)); padding: 4px 10px; border-radius: 999px; display: inline-flex; align-items: center; gap: 6px;">
            <span>🌀</span><span>Errorborn Quest</span>
          </div>
          <div style="font-size: 14px; font-weight: 600;">${escapeHtml(title)}</div>
        </div>
        <div style="font-size: 14px; font-weight: 600;">${collected} / ${requiredFragments}</div>
      </div>
      <div style="margin-top: 10px;">
        <div style="height: 8px; border-radius: 999px; background: rgba(156, 39, 176, 0.15); overflow: hidden;">
          <div style="width: ${progress}%; height: 100%; background: linear-gradient(135deg, rgba(156, 39, 176, 0.8), rgba(33, 150, 243, 0.9)); transition: width 0.3s ease;"></div>
        </div>
      </div>
      ${infoText}
      <button class="btn" onclick="showScreen('errorborn')" style="width: 100%; margin-top: 12px;">${escapeHtml(buttonText)}</button>
    `;
  }

  /**
   * Рендерит экран хроник Errorborn
   */
  function renderErrorbornScreen(status, storyText = []) {
    const container = documentRef.getElementById("errorborn-screen-content");
    if (!container) return;

    const fragmentLegend = status.fragmentLegend || [];
    const fragmentsMap = new Map(
      (status.fragments || []).map((fragment) => [fragment.code, fragment]),
    );

    const fragmentsHtml = fragmentLegend
      .map((legend) => {
        const hasFragment = fragmentsMap.has(legend.code);
        const fragment = fragmentsMap.get(legend.code);
        const acquiredAt = hasFragment
          ? formatDateFn(fragment?.acquiredAt)
          : null;
        const condition = legend.condition
          ? `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 2px;">${escapeHtml(legend.condition)}</div>`
          : "";

        return `
          <div style="display: flex; gap: 10px; padding: 10px; border-radius: 10px; background: ${hasFragment ? "rgba(76, 175, 80, 0.12)" : "rgba(156, 39, 176, 0.12)"}; border: 1px solid ${hasFragment ? "rgba(76, 175, 80, 0.35)" : "rgba(156, 39, 176, 0.25)"};">
            <div style="font-size: 20px; line-height: 1;">${hasFragment ? "✨" : "○"}</div>
            <div>
              <div style="font-weight: 600;">${escapeHtml(legend.name || legend.baseId || legend.code)}</div>
              ${condition}
              ${hasFragment && acquiredAt ? `<div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">Получено: ${acquiredAt}</div>` : ""}
            </div>
          </div>
        `;
      })
      .join("");

    let actionButton = "";
    if (status.state === "portal_ready" && status.claimable) {
      actionButton = `
        <button class="btn" id="errorborn-claim-button" onclick="claimErrorbornReward()" style="width: 100%; margin-top: 16px;">
          🔓 Призвать Errorborn
        </button>`;
    } else if (status.state === "claimed") {
      actionButton = `
        <div style="margin-top: 16px; padding: 12px; border-radius: 10px; background: rgba(76, 175, 80, 0.12); border: 1px solid rgba(76, 175, 80, 0.25);">
          ✅ Errorborn призван ${status.claimedAt ? `(${formatDateFn(status.claimedAt)})` : ""}
        </div>`;
    } else {
      actionButton = `
        <div style="margin-top: 16px; font-size: 13px; color: var(--tg-theme-hint-color);">
          Продолжайте сражения на арене The Stack. Эволюции Глитчлингов открывают новые фрагменты Errorborn.
        </div>`;
    }

    const timelineHtml = `
      <div style="display: grid; gap: 8px; margin-top: 16px;">
        <div style="font-size: 13px; color: var(--tg-theme-hint-color);">
          🔑 Портал: ${status.portalOpenedAt ? formatDateFn(status.portalOpenedAt) : "не открыт"}
        </div>
        <div style="font-size: 13px; color: var(--tg-theme-hint-color);">
          🧬 Призыв: ${status.claimedAt ? formatDateFn(status.claimedAt) : "ещё не проведён"}
        </div>
      </div>`;

    const storyHtml = Array.isArray(storyText) && storyText.length > 0
      ? `<div style="padding: 12px; border-radius: 12px; background: rgba(33, 150, 243, 0.08); border: 1px solid rgba(33, 150, 243, 0.2);">
          ${storyText.map((text) => `<p style="margin: 0 0 8px 0;">${escapeHtml(text)}</p>`).join("")}
        </div>`
      : "";

    container.innerHTML = `
      <div style="display: flex; flex-direction: column; gap: 16px;">
        <div>
          <div style="font-size: 16px; font-weight: 700;">Протокол Errorborn</div>
          <div style="font-size: 13px; color: var(--tg-theme-hint-color); margin-top: 4px;">
            Соберите фрагменты легендарного демона, чтобы открыть портал и призвать Errorborn в свою коллекцию.
          </div>
        </div>
        <div style="display: grid; gap: 10px;">
          ${fragmentsHtml}
        </div>
        ${timelineHtml}
        ${storyHtml}
        ${actionButton}
      </div>
    `;
  }

  return {
    renderErrorbornQuestCard,
    renderErrorbornScreen,
  };
}














